import { confirmAction } from '@functions/entity'

import {
  Box, Button, Form, SectionDivider, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import type { SidebarModalProps } from '@domain-group/suit-theme'

import useProvider, { useProviderForm } from '@hooks/useProvider'

import EntitySelectBox from '@components/EntitySelectBox'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

type ModalCallbacks = {
  closeModal: () => void,
  createProvider: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
}

const CreateProviderModal = (props: SidebarModalProps & ModalProps) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal } = callbacks

  const {
    callbacks: { createProvider: createFn },
    creating,
  } = useProvider()

  const {
    entityState,
    entityState: {
      address_line_1,
      address_postcode,
      address_region,
      address_state,
      business_number,
      email,
      geo_country_id,
      phone,
      title,
      website,
    },
    errors,
    handlers,
    saveEnabled,
  } = useProviderForm({}, { validateOn: 'change' })

  const confirmProviderCreatePayload = {
    callbacks: {
      actionFn: createFn,
      afterAction: closeModal,
    },
    options: {
      icon: 'warning',
      entityParams: entityState,
      shouldRedirect: true,
    },
    strings: {
      actionString: 'create',
      confirmString: `If this entity needs to be invoiced for any reason DO NOT proceed with creation.
      Billing Entities must be created via Salesforce.`,
      entityName: 'provider',
      toastText: 'Provider Created',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Provider" />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>Billing Entities MUST be created via Salesforce</SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            If this entity needs to be invoiced for any reason DO NOT proceed with creation.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>
        <Box flexDirection="column" flexShrink={0}>

          <Form>
            <Form.Field errorMessage={errors.title} label="* Title">
              <input name="title" value={title} type="text" {...handlers} />
            </Form.Field>

            <Form.Field errorMessage={errors.email} label="* Email" marginTop="large">
              <input
                data-validate-field-on="blur"
                name="email"
                type="email"
                value={email}
                {...handlers}
              />
            </Form.Field>

            <Form.Field errorMessage={errors.website} label="* Website" marginTop="large">
              <input name="website" value={website} type="text" {...handlers} />
            </Form.Field>

            <Form.Field errorMessage={errors.business_number} label="* Business Number" marginTop="large">
              <input name="business_number" value={business_number} type="text" {...handlers} />
            </Form.Field>

            <Form.Field errorMessage={errors.phone} label="* Phone" marginTop="large">
              <input name="phone" value={phone} type="text" {...handlers} />
            </Form.Field>

            <SectionDivider marginTop="large">BUSINESS ADDRESS</SectionDivider>

            <Form.Field errorMessage={errors.address_line_1} label="* Address Line 1" marginTop="large">
              <input name="address_line_1" value={address_line_1} type="text" {...handlers} />
            </Form.Field>

            <Form.Row marginTop="large">
              <Form.Field errorMessage={errors.address_region} hideErrorMessage label="* Suburb/Region">
                <input name="address_region" value={address_region} type="text" {...handlers} />
              </Form.Field>

              <Form.Field
                errorMessage={errors.address_state}
                hideErrorMessage
                label="* State"
                // @ts-expect-error TS2322
                marginTop={['large', 0]}
              >
                <input name="address_state" value={address_state} type="text" {...handlers} />
              </Form.Field>
            </Form.Row>

            <Form.Row marginTop="large">
              <Form.Field errorMessage={errors.address_postcode} hideErrorMessage label="* Postcode">
                <input name="address_postcode" value={address_postcode} type="text" {...handlers} />
              </Form.Field>

              <Form.Field
                errorMessage={errors.geo_country_id}
                hideErrorMessage
                label="* Country"
                // @ts-expect-error TS2322
                marginTop={['large', 0]}
              >
                <EntitySelectBox
                  data-value-type="number"
                  entityKey="geoCountries"
                  // @ts-expect-error TS2322
                  name="geo_country_id"
                  sortDirection="asc"
                  sortKey="title"
                  value={geo_country_id}
                  {...handlers}
                />
              </Form.Field>
            </Form.Row>
          </Form>
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          // @ts-expect-error TS2345
          onClick={() => confirmAction(confirmProviderCreatePayload)}
          size="large"
        >
          {saveEnabled ? 'Create Provider' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: SidebarModalProps & ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateProviderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
